import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listStoneRequirements } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import StoneTabs from '@/components/stones/StoneTabs';
import HeroThumb from '@/components/stones/HeroThumb';
import { STONE_REQUIREMENT_STATUSES, STONE_KINDS } from '@/utils/constants';
import { Gem, Plus, AlertTriangle, Users } from 'lucide-react';

const STATUS_LABEL = Object.fromEntries(STONE_REQUIREMENT_STATUSES.map(s => [s.value, s]));
const KIND_LABEL = Object.fromEntries(STONE_KINDS.map(k => [k.value, k.label]));

export default function StoneRequirementsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSupplier = user?.role === 'stone_supplier';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const fetch = async () => {
    try {
      const res = await listStoneRequirements(filter || undefined);
      setItems(res.data.data || []);
    } catch (e) { toast.error('Failed to load requirements'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <CollapsibleLayout>
      <StoneTabs active="requirements" />
      <div className="max-w-[1400px] mx-auto p-4 sm:p-6 space-y-4">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Gem size={20} className="text-teal-600" />
            <div>
              <h1 className="text-lg font-semibold">Stone Requirements</h1>
              <p className="text-xs text-muted-foreground">{isSupplier ? 'Requirements you have been invited to quote on' : 'RFQs you have posted to suppliers'}</p>
            </div>
          </div>
          {!isSupplier && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => navigate('/stones/suppliers')} data-testid="manage-suppliers">
                <Users size={14} className="mr-1" /> Suppliers
              </Button>
              <Button onClick={() => navigate('/stones/new')} className="bg-teal-600 hover:bg-teal-700" data-testid="new-requirement-btn">
                <Plus size={14} className="mr-1" /> New Requirement
              </Button>
            </div>
          )}
        </header>

        <div className="flex gap-2">
          {[{ value: '', label: 'All' }, ...STONE_REQUIREMENT_STATUSES].map(s => (
            <button key={s.value} onClick={() => setFilter(s.value)}
              className={`text-xs px-3 py-1 rounded border ${filter === s.value ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              data-testid={`filter-${s.value || 'all'}`}>
              {s.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-8">Loading…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border">
            <Gem size={48} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-sm">{isSupplier ? 'No requirements assigned to you yet.' : 'No requirements yet. Create your first one.'}</p>
          </div>
        ) : (
          <>
            {/* Mobile: card list (suppliers mostly use phones) */}
            <div className="md:hidden space-y-2">
              {items.map(it => {
                const status = STATUS_LABEL[it.status] || { label: it.status, color: '' };
                return (
                  <button key={it.id} onClick={() => navigate(`/stones/${it.id}`)}
                    className="w-full bg-white rounded-lg border p-3 text-left active:bg-teal-50/60"
                    data-testid={`req-card-${it.id}`}>
                    <div className="flex items-start gap-3">
                      <HeroThumb image={it.sample_images?.[0]} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-sm">{it.requirement_no}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded border ${status.color}`}>{status.label}</span>
                          {isSupplier && it.has_my_quote && <span className="text-[10px] text-emerald-700 font-medium">✓ Quoted</span>}
                        </div>
                        <div className="text-sm font-medium mt-1">{it.gemstone_type}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {KIND_LABEL[it.stone_kind] || it.stone_kind}
                          {it.size_mm ? ` · ${it.size_mm}` : ''}
                          {it.qty ? ` · Qty ${it.qty}` : ''}
                          {it.total_ctw ? ` · ${it.total_ctw} ct` : ''}
                        </div>
                      </div>
                      {!isSupplier && (
                        <div className="text-right text-xs">
                          <div className="text-[10px] text-muted-foreground">Quotes</div>
                          <div className="font-semibold">{it.quote_count || 0}</div>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            {/* Desktop: table */}
            <div className="hidden md:block bg-white rounded-lg border overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
                    <th className="text-left px-2 py-2 w-12"></th>
                    <th className="text-left px-3 py-2">Req No</th>
                    <th className="text-left px-3 py-2">Stone</th>
                    <th className="text-left px-3 py-2">Kind</th>
                    <th className="text-left px-3 py-2">Size</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-right px-3 py-2">Total CT</th>
                    <th className="text-center px-3 py-2">Status</th>
                    <th className="text-center px-3 py-2">Quotes</th>
                    <th className="text-left px-3 py-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => {
                    const status = STATUS_LABEL[it.status] || { label: it.status, color: '' };
                    return (
                      <tr key={it.id}
                        onClick={() => navigate(`/stones/${it.id}`)}
                        className="border-b hover:bg-teal-50/50 cursor-pointer"
                        data-testid={`req-row-${it.id}`}>
                        <td className="px-2 py-1.5"><HeroThumb image={it.sample_images?.[0]} size={36} /></td>
                        <td className="px-3 py-2 font-mono font-bold">{it.requirement_no}</td>
                        <td className="px-3 py-2">{it.gemstone_type}</td>
                        <td className="px-3 py-2 text-muted-foreground">{KIND_LABEL[it.stone_kind] || it.stone_kind}</td>
                        <td className="px-3 py-2">{it.size_mm || '—'}</td>
                        <td className="px-3 py-2 text-right">{it.qty ?? '—'}</td>
                        <td className="px-3 py-2 text-right">{it.total_ctw ?? '—'}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`text-[10px] px-2 py-0.5 rounded border ${status.color}`}>{status.label}</span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          {isSupplier && it.has_my_quote && <span className="text-[10px] text-emerald-700">✓</span>}
                          {!isSupplier && (it.quote_count || 0)}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground text-xs">{new Date(it.created_at).toLocaleDateString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </CollapsibleLayout>
  );
}

